// @flow
import React from 'react'
import I18n from 'i18n-react'
import { humanizer } from 'devx-js-utilities'

// core
import { WEB_TYPES_KEYS } from '../Redux/SettingsRedux'
import moment from '../Moment'

// types
import type { InsuranceCompanyType } from '../Types/InsuranceCompanyTypes'
import type { TariffContentWithKeyType } from '../Types/TariffsContentType'
import type QuoteInstance from '../Controllers/QuoteInstance'

type TravelSummaryPanelProps = {
  insuranceCompany: InsuranceCompanyType,
  propertiesConfig: { options: Array<any> },
  selectedQuote: QuoteInstance,
  tariffContent: TariffContentWithKeyType,
  userData: Object,
  webType: number,
}

const InsuranceDate = ({ insuranceStart, insuranceEnd, insuranceType, calculatorName }) => {
  if (insuranceType === 'repeated-trips') {
    if (['TravelCPP', 'TravelCSOB'].includes(calculatorName)) {
      return I18n.translate('contractDraftScreen.repeatedWithStart', {
        insuranceStart: moment(insuranceStart, 'YYYY-MM-DDTHH:mm').format('DD.MM.YYYY'),
      })
    }
    return `${moment(insuranceStart, 'YYYY-MM-DDTHH:mm').format('DD.MM.YYYY')}\u00a0-\u00a0${I18n.translate(
      'general.indefinitely',
    )}`
  } else if (insuranceType === 'yearly') {
    return I18n.translate('contractDraftScreen.yearlyWithStart', {
      insuranceStart: moment(insuranceStart, 'YYYY-MM-DDTHH:mm').format('DD.MM.YYYY'),
    })
  }
  return `${moment(insuranceStart, 'YYYY-MM-DDTHH:mm').format('DD.MM.YYYY')}\u00a0-\u00a0${moment(
    insuranceEnd,
    'YYYY-MM-DDTHH:mm',
  ).format('DD.MM.YYYY')}`
}

const TravelSummaryPanel = (props: TravelSummaryPanelProps) => {
  if (!props.tariffContent.default || !Object.keys(props.tariffContent.default).length) {
    return null
  }

  let properties = []
  if (props.propertiesConfig && props.propertiesConfig.options) {
    properties = props.propertiesConfig.options
      .filter((property) => !['injuryInsurance', 'liabilityInsurance'].includes(property.key))
      .map((property) => ({
        ...property,
        active: !!props.tariffContent.default.properties[property.key],
      }))
  }

  const price = `${humanizer.csNumber(props.selectedQuote.price)} ${I18n.translate('general.currency')}`

  const renderInsuranceWithPrice = (price: number | string) => {
    if (typeof price === 'number') {
      return <strong>{`${humanizer.csNumber(price)}\u00a0${I18n.translate('general.currency')}`}</strong>
    }
    if (typeof price === 'string') {
      return <strong>{price}</strong>
    }
    return <strong>{I18n.translate('general.notContain')}</strong>
  }

  let customIcon = ''
  if (props.tariffContent.default.icon && props.tariffContent.default.icon[WEB_TYPES_KEYS[props.webType || 0]]) {
    customIcon = props.tariffContent.default.icon[WEB_TYPES_KEYS[props.webType || 0]]
  }

  const medicalExpenses = props.tariffContent.default.getIn(['properties', 'medicalExpenses'], 0)
  const injuryInsurance = props.tariffContent.default.getIn(['properties', 'injuryInsurance'], 0)
  const liabilityInsurance = props.tariffContent.default.getIn(['properties', 'liabilityInsurance'], 0)

  return (
    <div className="bg-default-bg p-2 p-md-3">
      <div className="row align-items-center">
        <div className="col-12 mb-2 mb-lg-0 text-center text-lg-left">
          <strong className="lead">{I18n.translate('general.chosenInsurance')}</strong>
        </div>
        <div className="col-12 col-lg-1 mb-2 mb-lg-0">
          <div className="bg-transparent bg-lg-light border-0 d-inline-block">
            <img
              alt={props.insuranceCompany.name}
              src={customIcon ? customIcon : props.insuranceCompany.webLogo}
              width={60}
            />
          </div>
        </div>
        <div className="col-12 col-lg-11 pl-lg-0 mb-2 mb-lg-0 order-2 order-lg-0 mt-3 mt-lg-0">
          <div className="d-inline-block">
            <strong>
              {props.selectedQuote.calculatorName.includes('Suri')
                ? `${I18n.translate('general.productInCooperation')} `
                : ''}
              {props.insuranceCompany.name}
            </strong>
            <div className="mt-1 d-flex flex-wrap justify-content-between">
              {props.userData && props.userData.trip.country && (
                <span>{I18n.translate(`valueTranslations.country.${props.userData.trip.country}`)}</span>
              )}
              &nbsp;
              <span>
                <InsuranceDate
                  calculatorName={props.selectedQuote.calculatorName}
                  insuranceEnd={props.userData.getIn(['trip', 'insuranceEnd'])}
                  insuranceStart={props.userData.getIn(['trip', 'insuranceStart'])}
                  insuranceType={props.userData.getIn(['trip', 'insuranceType'])}
                />
              </span>
            </div>
          </div>
          <div className="d-inline-block ml-5">
            <span className="lead pr-5 pr-lg-0 font-weight-bold">
              {props.tariffContent.default.name[WEB_TYPES_KEYS[props.webType || 0]]}
            </span>
          </div>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-12 col-lg-5">
          <div className="py-1 px-3">
            <div className="row align-items-center h-100 w-100">
              <div className="col-2 text-center">
                <span className="pfp pfp-health align-middle h1" />
              </div>
              <div className="col-5">
                <span>{I18n.translate('quoteColumn.medicalExpenses')}</span>
              </div>
              <div className="col-5 text-right">{renderInsuranceWithPrice(medicalExpenses)}</div>
            </div>
          </div>
          <div className="py-1 px-3">
            <div className="row align-items-center h-100 w-100">
              <div className="col-2 text-center">
                <span className="pfp pfp-fracture align-middle h1" />
              </div>
              <div className="col-5">
                <span>{I18n.translate('quoteColumn.injuryInsurance')}</span>
              </div>
              <div className="col-5 text-right">{renderInsuranceWithPrice(injuryInsurance)}</div>
            </div>
          </div>
          <div className="py-1 px-3">
            <div className="row align-items-center h-100 w-100">
              <div className="col-2 text-center">
                <span className="pfp pfp-shaking-hands align-middle h1" />
              </div>
              <div className="col-5">
                <span>{I18n.translate('quoteColumn.liabilityInsurance')}</span>
              </div>
              <div className="col-5 text-right">{renderInsuranceWithPrice(liabilityInsurance)}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-5">
          {properties.map((property) => (
            <div className="py-1 px-3" key={property.key}>
              <div className="row align-items-center h-100 w-100">
                <div className="col-2 text-center">
                  <span className={`${property.icon} align-middle h1`} />
                </div>
                <div className="col-5">
                  <span>{I18n.translate(`filter.values.${property.key}`)}</span>
                </div>
                <div className="col-5 text-right text-lg-left">
                  <span
                    className={property.active ? 'pfp pfp-check text-success h1' : 'pfp pfp-close text-danger h1'}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-12 col-lg-11 offset-lg-1 pl-lg-0 text-right text-lg-left">
          <strong className="lead pr-5 pr-lg-0 font-weight-bold">
            {I18n.translate('general.price')}:&nbsp;
            {props.userData.trip.insuranceType === 'short-term'
              ? price
              : I18n.translate('general.pricePerYear', { price })}
          </strong>
        </div>
      </div>
    </div>
  )
}

export default TravelSummaryPanel
